import * as React from "react";
import { Combobox } from "@base-ui/react/combobox";
import Checkbox from "@sproutsocial/seeds-react-checkbox";
import { cn, type WithStringClassName } from "./cn";

/**
 * v3 Combobox / Searchable Select surface, rendered with CSS classes from
 * menu.css (imported via @sproutsocial/seeds-react-menu/base/css) instead of
 * styled-components. Each export keeps its previous name and forwards
 * props/refs to the underlying Base UI component; the transient `$isInvalid`
 * prop is translated to a modifier class here so call sites are unchanged.
 */

export const ComboboxLabel = React.forwardRef<
  HTMLLabelElement,
  React.ComponentProps<"label">
>(({ className, ...rest }, ref) => (
  <label ref={ref} className={cn("seeds-menu-label", className)} {...rest} />
));
ComboboxLabel.displayName = "ComboboxLabel";

type InputGroupProps = WithStringClassName<
  React.ComponentProps<typeof Combobox.InputGroup>
> & {
  $isInvalid?: boolean;
};

export const ComboboxInputGroup = React.forwardRef<
  React.ElementRef<typeof Combobox.InputGroup>,
  InputGroupProps
>(({ $isInvalid, className, ...rest }, ref) => (
  <Combobox.InputGroup
    ref={ref}
    className={cn(
      "seeds-combobox-input-group",
      { "seeds-combobox-input-group--invalid": !!$isInvalid },
      className
    )}
    {...rest}
  />
));
ComboboxInputGroup.displayName = "ComboboxInputGroup";

export const ComboboxInput = React.forwardRef<
  React.ElementRef<typeof Combobox.Input>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Input>>
>(({ className, ...rest }, ref) => (
  <Combobox.Input
    ref={ref}
    className={cn("seeds-combobox-input", className)}
    {...rest}
  />
));
ComboboxInput.displayName = "ComboboxInput";

export const ComboboxTokenInput = React.forwardRef<
  React.ElementRef<typeof Combobox.Input>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Input>>
>(({ className, ...rest }, ref) => (
  <Combobox.Input
    ref={ref}
    className={cn("seeds-combobox-token-input", className)}
    {...rest}
  />
));
ComboboxTokenInput.displayName = "ComboboxTokenInput";

export const ComboboxActionButtons = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn("seeds-combobox-action-buttons", className)}
    {...rest}
  />
));
ComboboxActionButtons.displayName = "ComboboxActionButtons";

export const ComboboxClear = React.forwardRef<
  React.ElementRef<typeof Combobox.Clear>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Clear>>
>(({ className, ...rest }, ref) => (
  <Combobox.Clear
    ref={ref}
    tabIndex={0}
    className={cn("seeds-combobox-clear", className)}
    {...rest}
  />
));
ComboboxClear.displayName = "ComboboxClear";

export const ComboboxTrigger = React.forwardRef<
  React.ElementRef<typeof Combobox.Trigger>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Trigger>>
>(({ className, ...rest }, ref) => (
  <Combobox.Trigger
    ref={ref}
    className={cn("seeds-combobox-trigger", className)}
    {...rest}
  />
));
ComboboxTrigger.displayName = "ComboboxTrigger";

export const ComboboxPositioner = React.forwardRef<
  React.ElementRef<typeof Combobox.Positioner>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Positioner>>
>(({ className, ...rest }, ref) => (
  <Combobox.Positioner
    ref={ref}
    className={cn("seeds-combobox-positioner", className)}
    {...rest}
  />
));
ComboboxPositioner.displayName = "ComboboxPositioner";

export const ComboboxPopup = React.forwardRef<
  React.ElementRef<typeof Combobox.Popup>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Popup>>
>(({ className, ...rest }, ref) => (
  <Combobox.Popup
    ref={ref}
    className={cn("seeds-combobox-popup", className)}
    {...rest}
  />
));
ComboboxPopup.displayName = "ComboboxPopup";

export const ComboboxList = React.forwardRef<
  React.ElementRef<typeof Combobox.List>,
  WithStringClassName<React.ComponentProps<typeof Combobox.List>>
>(({ className, ...rest }, ref) => (
  <Combobox.List
    ref={ref}
    className={cn("seeds-combobox-list", className)}
    {...rest}
  />
));
ComboboxList.displayName = "ComboboxList";

export const ComboboxStatus = React.forwardRef<
  React.ElementRef<typeof Combobox.Status>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Status>>
>(({ className, ...rest }, ref) => (
  <Combobox.Status
    ref={ref}
    className={cn("seeds-combobox-status", className)}
    {...rest}
  />
));
ComboboxStatus.displayName = "ComboboxStatus";

export const ComboboxEmpty = React.forwardRef<
  React.ElementRef<typeof Combobox.Empty>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Empty>>
>(({ className, ...rest }, ref) => (
  <Combobox.Empty
    ref={ref}
    className={cn("seeds-combobox-empty", className)}
    {...rest}
  />
));
ComboboxEmpty.displayName = "ComboboxEmpty";

export const ComboboxItem = React.forwardRef<
  React.ElementRef<typeof Combobox.Item>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Item>>
>(({ className, ...rest }, ref) => (
  <Combobox.Item
    ref={ref}
    className={cn("seeds-combobox-item", className)}
    {...rest}
  />
));
ComboboxItem.displayName = "ComboboxItem";

export const ComboboxItemIndicator = React.forwardRef<
  React.ElementRef<typeof Combobox.ItemIndicator>,
  WithStringClassName<React.ComponentProps<typeof Combobox.ItemIndicator>>
>(({ className, ...rest }, ref) => (
  <Combobox.ItemIndicator
    ref={ref}
    className={cn("seeds-combobox-item-indicator", className)}
    {...rest}
  />
));
ComboboxItemIndicator.displayName = "ComboboxItemIndicator";

export const ComboboxGroup = React.forwardRef<
  React.ElementRef<typeof Combobox.Group>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Group>>
>(({ className, ...rest }, ref) => (
  <Combobox.Group
    ref={ref}
    className={cn("seeds-combobox-group", className)}
    {...rest}
  />
));
ComboboxGroup.displayName = "ComboboxGroup";

export const ComboboxGroupLabel = React.forwardRef<
  React.ElementRef<typeof Combobox.GroupLabel>,
  WithStringClassName<React.ComponentProps<typeof Combobox.GroupLabel>>
>(({ className, ...rest }, ref) => (
  <Combobox.GroupLabel
    ref={ref}
    className={cn("seeds-combobox-group-label", className)}
    {...rest}
  />
));
ComboboxGroupLabel.displayName = "ComboboxGroupLabel";

export const ComboboxSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn("seeds-combobox-separator", className)}
    {...rest}
  />
));
ComboboxSeparator.displayName = "ComboboxSeparator";

export const ComboboxGroupHeaderItem = React.forwardRef<
  React.ElementRef<typeof Combobox.Item>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Item>>
>(({ className, ...rest }, ref) => (
  <Combobox.Item
    ref={ref}
    className={cn("seeds-combobox-group-header-item", className)}
    {...rest}
  />
));
ComboboxGroupHeaderItem.displayName = "ComboboxGroupHeaderItem";

export const ComboboxSelectAllItem = React.forwardRef<
  React.ElementRef<typeof Combobox.Item>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Item>>
>(({ className, ...rest }, ref) => (
  <Combobox.Item
    ref={ref}
    className={cn("seeds-combobox-select-all-item", className)}
    {...rest}
  />
));
ComboboxSelectAllItem.displayName = "ComboboxSelectAllItem";

export function ComboboxGroupHeaderCheckbox({
  $state,
  id,
}: {
  $state: "none" | "partial" | "all";
  id: string;
}) {
  return (
    <Checkbox
      id={id}
      checked={$state === "all"}
      indeterminate={$state === "partial"}
      onChange={() => {}}
      tabIndex={-1}
    />
  );
}

// Multi-select Token components
export const ComboboxToken = React.forwardRef<
  React.ElementRef<typeof Combobox.Chips>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Chips>>
>(({ className, ...rest }, ref) => (
  <Combobox.Chips
    ref={ref}
    className={cn("seeds-combobox-tokens", className)}
    {...rest}
  />
));
ComboboxToken.displayName = "ComboboxToken";

type TokenInputGroupProps = WithStringClassName<
  React.ComponentProps<typeof Combobox.InputGroup>
> & {
  $isInvalid?: boolean;
};

export const ComboboxTokenInputGroup = React.forwardRef<
  React.ElementRef<typeof Combobox.InputGroup>,
  TokenInputGroupProps
>(({ $isInvalid, className, ...rest }, ref) => (
  <Combobox.InputGroup
    ref={ref}
    className={cn(
      "seeds-combobox-token-input-group",
      { "seeds-combobox-token-input-group--invalid": !!$isInvalid },
      className
    )}
    {...rest}
  />
));
ComboboxTokenInputGroup.displayName = "ComboboxTokenInputGroup";

export const Token = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<"span">
>(({ className, ...rest }, ref) => (
  <span ref={ref} className={cn("seeds-combobox-token", className)} {...rest} />
));
Token.displayName = "Token";

export const TokenRemove = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button">
>(({ className, ...rest }, ref) => (
  <button
    ref={ref}
    className={cn("seeds-combobox-token-remove", className)}
    {...rest}
  />
));
TokenRemove.displayName = "TokenRemove";

// Searchable select components

type SearchableTriggerProps = WithStringClassName<
  React.ComponentProps<typeof Combobox.Trigger>
> & {
  $isInvalid?: boolean;
};

export const SearchableSelectTrigger = React.forwardRef<
  React.ElementRef<typeof Combobox.Trigger>,
  SearchableTriggerProps
>(({ $isInvalid, className, ...rest }, ref) => (
  <Combobox.Trigger
    ref={ref}
    className={cn(
      "seeds-searchable-select-trigger",
      { "seeds-searchable-select-trigger--invalid": !!$isInvalid },
      className
    )}
    {...rest}
  />
));
SearchableSelectTrigger.displayName = "SearchableSelectTrigger";

export const SearchableSelectTriggerIcon = React.forwardRef<
  React.ElementRef<typeof Combobox.Icon>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Icon>>
>(({ className, ...rest }, ref) => (
  <Combobox.Icon
    ref={ref}
    className={cn("seeds-searchable-select-trigger-icon", className)}
    {...rest}
  />
));
SearchableSelectTriggerIcon.displayName = "SearchableSelectTriggerIcon";

export const SearchableSelectPlaceholder = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<"span">
>(({ className, ...rest }, ref) => (
  <span
    ref={ref}
    className={cn("seeds-searchable-select-placeholder", className)}
    {...rest}
  />
));
SearchableSelectPlaceholder.displayName = "SearchableSelectPlaceholder";

export const SearchableSelectPopup = ComboboxPopup;

export const SearchableSelectSearchContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn("seeds-searchable-select-search-container", className)}
    {...rest}
  />
));
SearchableSelectSearchContainer.displayName = "SearchableSelectSearchContainer";

export const SearchableSelectInput = React.forwardRef<
  React.ElementRef<typeof Combobox.Input>,
  WithStringClassName<React.ComponentProps<typeof Combobox.Input>>
>(({ className, ...rest }, ref) => (
  <Combobox.Input
    ref={ref}
    className={cn("seeds-searchable-select-input", className)}
    {...rest}
  />
));
SearchableSelectInput.displayName = "SearchableSelectInput";

export const SearchableSelectList = React.forwardRef<
  React.ElementRef<typeof Combobox.List>,
  WithStringClassName<React.ComponentProps<typeof Combobox.List>>
>(({ className, ...rest }, ref) => (
  <Combobox.List
    ref={ref}
    className={cn("seeds-searchable-select-list", className)}
    {...rest}
  />
));
SearchableSelectList.displayName = "SearchableSelectList";

type TokenTriggerProps = WithStringClassName<
  React.ComponentProps<typeof Combobox.Trigger>
> & {
  $isInvalid?: boolean;
};

export const SearchableSelectTokenTrigger = React.forwardRef<
  React.ElementRef<typeof Combobox.Trigger>,
  TokenTriggerProps
>(({ $isInvalid, className, ...rest }, ref) => (
  <Combobox.Trigger
    ref={ref}
    className={cn(
      "seeds-searchable-select-token-trigger",
      { "seeds-searchable-select-token-trigger--invalid": !!$isInvalid },
      className
    )}
    {...rest}
  />
));
SearchableSelectTokenTrigger.displayName = "SearchableSelectTokenTrigger";
